import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  ReceiptText,
  Search,
  Plus,
  Trash2,
  UserSearch,
  AlertTriangle,
  PillBottle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Textarea } from '@/components/common/Textarea'
import { Badge } from '@/components/common/Badge'
import { patientApi } from '@/api/patientApi'
import { drugApi } from '@/api/drugApi'
import { billingApi } from '@/api/billingApi'
import { billCreateSchema } from '@/lib/validators'
import { useBillingStore } from '@/store/billingStore'
import { formatRupees, formatDate, formatMobile, cn, cleanPayload } from '@/lib/utils'

const TODAY_ISO = new Date().toISOString().slice(0, 10)

/* --------------------------- helper hooks --------------------------- */
function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return v
}

/* --------------------------- patient picker --------------------------- */
function PatientPicker({ patient, onPick, error }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debounced = useDebounced(query, 250)
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const boxRef = useRef()

  useEffect(() => {
    if (!debounced || debounced.length < 2) {
      setResults([])
      return
    }
    setSearching(true)
    patientApi
      .list({ search: debounced, limit: 6 })
      .then((rows) => setResults(rows))
      .catch(() => setResults([]))
      .finally(() => setSearching(false))
  }, [debounced])

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (patient) {
    return (
      <div className="rounded-xl bg-primary-50/50 p-3 ring-1 ring-primary-200">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-ink-900">{patient.name}</p>
            <p className="text-xs font-mono text-primary-800">{patient.patient_id}</p>
            <p className="mt-0.5 text-xs text-ink-500">
              {formatMobile(patient.mobile_number)} · {patient.gender} · age {patient.age}
            </p>
          </div>
          <Button variant="ghost" size="xs" onClick={() => onPick(null)}>
            Change
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div ref={boxRef} className="relative">
      <Input
        leftIcon={<UserSearch className="h-4 w-4" />}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search patient by name, ID or mobile"
        error={error}
      />
      {open && (query.length >= 2 || results.length > 0) && (
        <div className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-xl bg-white shadow-lift ring-1 ring-ink-100">
          {searching ? (
            <p className="px-4 py-3 text-xs text-ink-500">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-4 py-3 text-xs text-ink-500">No patient matched “{query}”.</p>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {results.map((p) => (
                <li key={p.patient_id}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick(p)
                      setOpen(false)
                      setQuery('')
                    }}
                    className="flex w-full items-start gap-2 px-4 py-2.5 text-left transition hover:bg-cream/70"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ink-900">{p.name}</p>
                      <p className="text-[11px] text-ink-500">
                        <span className="font-mono">{p.patient_id}</span> ·{' '}
                        {formatMobile(p.mobile_number)}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

/* --------------------------- medicine line --------------------------- */
function MedicinePicker({ onPick, blacklist }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debounced = useDebounced(query, 200)
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const boxRef = useRef()

  useEffect(() => {
    let cancelled = false
    setSearching(true)
    drugApi
      .list({ search: debounced || undefined, limit: 10 })
      .then((rows) => {
        if (cancelled) return
        const filtered = rows.filter(
          (r) => r.is_active !== false && !blacklist.includes(r.drug_id),
        )
        setResults(filtered)
      })
      .catch(() => !cancelled && setResults([]))
      .finally(() => !cancelled && setSearching(false))
    return () => {
      cancelled = true
    }
  }, [debounced, blacklist])

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={boxRef} className="relative">
      <Input
        leftIcon={<Search className="h-4 w-4" />}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search medicine to add (start typing)"
      />
      {open && (
        <div className="absolute z-10 mt-1.5 w-full overflow-hidden rounded-xl bg-white shadow-lift ring-1 ring-ink-100">
          {searching ? (
            <p className="px-4 py-3 text-xs text-ink-500">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-4 py-3 text-xs text-ink-500">No medicines found.</p>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {results.map((d) => {
                const oos = (d.stock_count || 0) === 0
                return (
                  <li key={d.drug_id}>
                    <button
                      type="button"
                      disabled={oos}
                      onClick={() => {
                        onPick(d)
                        setOpen(false)
                        setQuery('')
                      }}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-2.5 text-left transition',
                        oos
                          ? 'cursor-not-allowed opacity-60'
                          : 'hover:bg-cream/70',
                      )}
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                        <PillBottle className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink-900">{d.name}</p>
                        <p className="text-[11px] text-ink-500">
                          {formatRupees(d.mrp)} · Stock {d.stock_count}
                          {d.dosage ? ` · ${d.dosage}` : ''}
                        </p>
                      </div>
                      {oos && (
                        <Badge tone="danger" size="xs">
                          Out of stock
                        </Badge>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

/* --------------------------- main page --------------------------- */
export default function NewBill() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const prepend = useBillingStore((s) => s.prepend)

  const [patient, setPatient] = useState(null)
  const [items, setItems] = useState([]) // [{ drug, quantity }]
  const [serviceCharge, setServiceCharge] = useState('')
  const [paid, setPaid] = useState('')
  const [billDate, setBillDate] = useState(TODAY_ISO)
  const [treatmentPlan, setTreatmentPlan] = useState('')
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  // Pre-select patient if id came in via ?patient_id= or location.state
  useEffect(() => {
    const pid = searchParams.get('patient_id') || location.state?.patient_id
    if (!pid || patient) return
    patientApi
      .get(pid)
      .then((p) => setPatient(p))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const blacklist = useMemo(() => items.map((i) => i.drug.drug_id), [items])

  const totals = useMemo(() => {
    const medicine = items.reduce(
      (acc, i) => acc + Number(i.drug.mrp || 0) * Number(i.quantity || 0),
      0,
    )
    const service = Number(serviceCharge) || 0
    const total = medicine + service
    const paidNum = Number(paid) || 0
    return {
      medicine: Math.round(medicine * 100) / 100,
      service: Math.round(service * 100) / 100,
      total: Math.round(total * 100) / 100,
      balance: Math.round((total - paidNum) * 100) / 100,
    }
  }, [items, serviceCharge, paid])

  const addItem = (drug) => {
    setItems((prev) => [...prev, { drug, quantity: 1 }])
  }

  const updateQty = (idx, qty) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, quantity: qty } : it)),
    )
  }

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    setErrors({})
    if (!patient) {
      setErrors({ patient_id: 'Pick a patient' })
      return
    }

    // Client-side stock guard so the user doesn't hit a 400 from backend
    for (const it of items) {
      const q = Number(it.quantity) || 0
      if (q < 1) {
        toast.error(`Quantity for ${it.drug.name} must be at least 1`)
        return
      }
      if (q > it.drug.stock_count) {
        toast.error(
          `Only ${it.drug.stock_count} of ${it.drug.name} in stock`,
        )
        return
      }
    }

    const candidate = {
      patient_id: patient.patient_id,
      service_charge: Number(serviceCharge) || 0,
      paid: Number(paid) || 0,
      bill_date: billDate || undefined,
      treatment_plan: treatmentPlan,
      notes,
      items: items.map((it) => ({
        drug_id: it.drug.drug_id,
        quantity: Number(it.quantity),
      })),
    }
    const parsed = billCreateSchema.safeParse(candidate)
    if (!parsed.success) {
      const errs = {}
      parsed.error.issues.forEach((iss) => {
        errs[iss.path.join('.')] = iss.message
      })
      setErrors(errs)
      const first = parsed.error.issues[0]
      if (first) toast.error(first.message)
      return
    }

    setSubmitting(true)
    try {
      const payload = cleanPayload(parsed.data) || parsed.data
      // Don't strip items if the user explicitly added them but cleanPayload kept it.
      if (!payload.items && items.length > 0) {
        payload.items = parsed.data.items
      }
      const bill = await billingApi.create(payload)
      prepend(bill)
      toast.success(`Bill ${bill.bill_id} created`)
      navigate(`/billing/${bill.bill_id}`)
    } catch {
      // toast handled by axios interceptor
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="container-page py-6 sm:py-8">
      <PageHeader
        eyebrow="Phase 7 — Billing"
        title="Create a new bill"
        description="Service charge is manual. Medicine charge auto-computes from inventory MRP and stock is deducted on save."
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        {/* Left column — patient + items + meta */}
        <div className="space-y-5">
          {/* Patient */}
          <section className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-ink-100">
            <p className="label-text mb-2">Patient</p>
            <PatientPicker patient={patient} onPick={setPatient} error={errors.patient_id} />
          </section>

          {/* Medicines */}
          <section className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-ink-100">
            <p className="label-text mb-2">Medicines dispensed</p>
            <MedicinePicker onPick={addItem} blacklist={blacklist} />
            {items.length === 0 ? (
              <p className="mt-4 rounded-xl bg-cream/60 p-3 text-xs text-ink-500 ring-1 ring-ink-100">
                No medicines added yet. Pick from the search above, or leave empty for a service-only bill.
              </p>
            ) : (
              <ul className="mt-4 space-y-2">
                {items.map((it, idx) => {
                  const qty = Number(it.quantity) || 0
                  const overstock = qty > it.drug.stock_count
                  const lineTotal = qty * Number(it.drug.mrp || 0)
                  return (
                    <li
                      key={it.drug.drug_id}
                      className={cn(
                        'grid gap-3 rounded-xl bg-cream/60 p-3 ring-1 ring-ink-100 sm:grid-cols-[1.6fr_110px_120px_auto] sm:items-center',
                        overstock && 'ring-rose-300 bg-rose-50/40',
                      )}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-ink-900">
                          {it.drug.name}
                        </p>
                        <p className="text-[11px] text-ink-500">
                          {formatRupees(it.drug.mrp)} / unit · Stock {it.drug.stock_count}
                        </p>
                        {overstock && (
                          <p className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-rose-600">
                            <AlertTriangle className="h-3 w-3" /> Only {it.drug.stock_count} available
                          </p>
                        )}
                      </div>
                      <Input
                        type="number"
                        min="1"
                        max={it.drug.stock_count}
                        value={it.quantity}
                        onChange={(e) => updateQty(idx, e.target.value)}
                      />
                      <div className="text-right">
                        <p className="text-[11px] text-ink-500">Line total</p>
                        <p className="font-semibold tabular-nums text-ink-900">
                          {formatRupees(lineTotal)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(idx)}
                        title="Remove"
                        className="text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          {/* Treatment / notes */}
          <section className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-ink-100">
            <p className="label-text mb-3">Visit context</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Bill date"
                type="date"
                value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
              />
              <div />
              <Textarea
                label="Treatment plan snapshot"
                rows={2}
                value={treatmentPlan}
                onChange={(e) => setTreatmentPlan(e.target.value)}
                placeholder="e.g. RCT + crown for 36"
                className="sm:col-span-2"
              />
              <Textarea
                label="Notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional internal note"
                className="sm:col-span-2"
              />
            </div>
          </section>
        </div>

        {/* Right column — totals + actions (sticky on desktop) */}
        <div>
          <div className="sticky top-4 space-y-3 rounded-2xl bg-white p-5 shadow-soft ring-1 ring-ink-100">
            <div className="flex items-center gap-2">
              <ReceiptText className="h-4 w-4 text-ink-500" />
              <p className="font-semibold text-ink-900">Bill summary</p>
            </div>

            <div className="grid gap-2.5 pt-1">
              <div>
                <label className="label-text">Service charge (₹)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={serviceCharge}
                  onChange={(e) => setServiceCharge(e.target.value)}
                  placeholder="0.00"
                  error={errors.service_charge}
                />
              </div>
              <div className="rounded-xl bg-cream/60 p-3 ring-1 ring-ink-100">
                <dl className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-ink-500">Service</dt>
                    <dd className="tabular-nums text-ink-800">
                      {formatRupees(totals.service)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-ink-500">Medicines</dt>
                    <dd className="tabular-nums text-ink-800">
                      {formatRupees(totals.medicine)}
                    </dd>
                  </div>
                  <div className="my-1 border-t border-ink-100" />
                  <div className="flex justify-between text-base">
                    <dt className="font-semibold text-ink-900">Total</dt>
                    <dd className="font-bold tabular-nums text-ink-900">
                      {formatRupees(totals.total)}
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                <label className="label-text">Paid (₹)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paid}
                  onChange={(e) => setPaid(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div
                className={cn(
                  'rounded-xl p-3 ring-1',
                  totals.balance > 0
                    ? 'bg-accent-50/60 ring-accent-200'
                    : 'bg-emerald-50/60 ring-emerald-200',
                )}
              >
                <div className="flex items-center justify-between text-sm">
                  <p className="font-semibold text-ink-900">Balance due</p>
                  <p
                    className={cn(
                      'text-base font-bold tabular-nums',
                      totals.balance > 0 ? 'text-accent-700' : 'text-emerald-700',
                    )}
                  >
                    {formatRupees(totals.balance)}
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={submitting}
              variant="accent"
              leftIcon={!submitting ? <Plus className="h-4 w-4" /> : null}
            >
              Save bill & dispense stock
            </Button>
            <p className="text-[11px] text-ink-500">
              Saving creates the bill and atomically deducts stock for every medicine.
              If any item fails, the whole bill is rolled back.
            </p>
          </div>
        </div>
      </form>
    </div>
  )
}
